import { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, FileText, Monitor, KeyRound, ShieldOff, 
  Gavel, LogOut, Mail, AlertCircle, CreditCard, RefreshCcw, ShieldCheck, HelpCircle
} from "lucide-react";
import { DPO_INFO } from "@/lib/security";
import { cn } from "@/lib/utils";

export default function TermosPage() {
  const isDark = false; 

  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EAF4FF] via-[#F5FAFF] to-[#E6F0FF] text-slate-900 font-sans selection:bg-blue-200 relative overflow-hidden pb-20">
      
      {/* Glass background orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#33CCFF33,transparent_60%)] blur-3xl" />
        <div className="absolute top-[20%] -right-40 size-[600px] rounded-full bg-[radial-gradient(circle_at_center,#1A66CC22,transparent_60%)] blur-3xl" />
        <div className="absolute bottom-[-10%] left-[10%] size-[520px] rounded-full bg-[radial-gradient(circle_at_center,#A5C8FF44,transparent_65%)] blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12 relative z-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 mb-8 group transition-all"
        >
          <div className="size-8 rounded-lg bg-white shadow-sm border border-blue-100 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
            <ArrowLeft className="size-4" />
          </div>
          Voltar para o Início
        </Link>

        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-br from-[#33CCFF]/10 to-[#1A66CC]/5 rounded-[2.5rem] blur-2xl opacity-50" />
          
          <div className="relative bg-white/60 backdrop-blur-3xl rounded-[2rem] border border-white/80 p-8 lg:p-12 shadow-[0_30px_80px_-20px_rgba(26,102,204,0.15),inset_0_1px_1px_rgba(255,255,255,1)]">
            
            <header className="flex flex-col sm:flex-row sm:items-center gap-5 mb-12 pb-8 border-b border-blue-100/50">
              <div className="size-14 rounded-2xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center text-white shadow-lg shadow-blue-500/30">
                <FileText className="size-7" />
              </div>
              <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-1">Termos de Uso</h1>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  Versão {DPO_INFO.versaoPolitica} · {DPO_INFO.ultimaAtualizacao}
                </p>
              </div>
            </header>

            <div className="prose prose-slate max-w-none space-y-10">
              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <Monitor className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">1. Objeto e Processamento</h2>
                </div>
                <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100/50 leading-relaxed text-sm text-slate-600">
                  O DataGrid XML disponibiliza ferramenta de inspeção sistêmica e cruzamento de dados de XMLs NF-e/NFC-e com
                  processamento <strong>exclusivamente local</strong>. O USUÁRIO declara estar ciente de que não há transmissão do
                  conteúdo integral dos arquivos para nossos servidores.
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <KeyRound className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">2. Responsabilidades</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  O usuário é responsável pelo sigilo da senha e pela integridade do dispositivo de acesso. 
                  A CONTRATADA não mantém backups dos dados processados em memória volátil.
                </p>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <ShieldOff className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">3. Limites Técnicos</h2>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl border border-slate-100 bg-white/40 text-center">
                    <div className="text-xs font-black text-blue-600 uppercase mb-1">Upload Lote</div>
                    <div className="text-lg font-black text-slate-900">200 MB</div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-white/40 text-center">
                    <div className="text-xs font-black text-blue-600 uppercase mb-1">XML Individual</div>
                    <div className="text-lg font-black text-slate-900">50 MB</div>
                  </div>
                  <div className="p-4 rounded-xl border border-slate-100 bg-white/40 text-center">
                    <div className="text-xs font-black text-blue-600 uppercase mb-1">Proteção</div>
                    <div className="text-lg font-black text-slate-900">XXE/ZIP</div>
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">4. Assinaturas e Pagamentos</h2>
                </div>
                <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                  <p>
                    O acesso às funcionalidades avançadas do DataGrid XML é disponibilizado mediante planos de assinatura recorrente (mensal ou anual). 
                    Os pagamentos são processados por parceiros especializados (como Stripe ou Asaas), garantindo a segurança de seus dados financeiros.
                  </p>
                  <p>
                    A renovação ocorre de forma automática ao final de cada período, salvo cancelamento prévio pelo usuário. 
                    Reservamo-nos o direito de reajustar valores, comunicando os usuários com antecedência mínima de 30 dias.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <RefreshCcw className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">5. Cancelamento e Reembolso</h2>
                </div>
                <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
                  <p>
                    <strong>Cancelamento:</strong> O usuário pode solicitar o cancelamento da recorrência a qualquer momento através do painel "Minha Assinatura". 
                    O acesso permanecerá ativo até o término do período já pago, sem reembolsos proporcionais por dias não utilizados.
                  </p>
                  <p>
                    <strong>Direito de Arrependimento:</strong> Conforme o Art. 49 do CDC, novos assinantes possuem o prazo de 7 (sete) dias corridos, 
                    a contar da primeira contratação, para desistir do serviço com reembolso integral. Esta regra aplica-se apenas à primeira compra e não a renovações.
                  </p>
                </div>
              </section>

              <section className="relative overflow-hidden group">
                <div className="absolute inset-0 bg-red-50/50 group-hover:bg-red-50 transition-colors -z-10 rounded-2xl border border-red-100" />
                <div className="p-6 lg:p-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="size-6 text-red-500" />
                    <h2 className="text-xl font-black text-red-900 m-0 uppercase tracking-tight">6. Isenção de Análise Regulatória</h2>
                  </div>
                  <p className="text-sm text-red-800 leading-relaxed font-medium italic">
                    "O DataGrid XML NÃO presta serviços de assessoria legal e NÃO substitui o trabalho de um Especialista em Regulação."
                  </p>
                  <p className="text-xs text-red-700/80 leading-relaxed">
                    O USUÁRIO concorda que a tomada de qualquer decisão fiscal, retificação de obrigações ou aproveitamento de créditos exige a análise, validação e assinatura de um Especialista em Regulação habilitado.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="size-5 text-blue-500" />
                  <h2 className="text-xl font-black text-slate-900 m-0">7. Propriedade Intelectual</h2>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Todos os algoritmos, interfaces e scripts de análise local são de propriedade exclusiva da CONTRATADA. 
                  É proibida qualquer tentativa de engenharia reversa, cópia ou distribuição comercial do software sem autorização prévia.
                </p>
              </section>

              <section className="grid sm:grid-cols-2 gap-8 pt-8 border-t border-blue-100/50">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <HelpCircle className="size-5 text-blue-500" />
                    <h3 className="text-lg font-black text-slate-900 m-0">Modificações</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Estes termos podem ser atualizados para refletir novas funcionalidades. O uso contínuo após a atualização implica aceitação dos novos termos.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Gavel className="size-5 text-blue-500" />
                    <h3 className="text-lg font-black text-slate-900 m-0">Foro</h3>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    Aplica-se a legislação brasileira; foro da comarca do estabelecimento da CONTRATADA para dirimir quaisquer controvérsias.
                  </p>
                </div>
              </section>

              <footer className="mt-10 pt-6 flex items-center gap-2 text-xs font-bold text-slate-400">
                <Mail className="size-4" />
                Dúvidas: <a href={`mailto:${DPO_INFO.email}`} className="text-blue-600 hover:underline">{DPO_INFO.email}</a>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
